import React, { useEffect, useState } from 'react'
import { useRouter } from 'next/router'

import { useAppSelector } from 'redux/hooks'

import { CollectionColumn, CollectionColumnOptions, SaturatedCollectionColumnOptions } from 'redux/collectionV2/types'
import { PRICE_CODES } from 'types/active'

import { CollectionCardWrapperChildProps } from 'components/collectionV2/views/CardWrapper'
import { GenericCursorCardWrapper } from 'components/card/CursorCardWrapper'

import { setCollectionQueryParams } from 'utils/queryParams/collection.queryParams'
import {
  AUTO_COLUMNS,
  generateSaturatedColumns,
  generateSaturatedHeaders,
} from 'components/collectionV2/views/Text/TextCollectionCard/columnDefinitions.utils'

import styles from './textCollectionCard.module.scss'

const TextCollectionCard = ({ collectionCard, actions, extraData, localCardData }: CollectionCardWrapperChildProps) => {
  const tags = useAppSelector(state => state.collectionV2.tags)
  const priceSource = useAppSelector(state => state.collectionV2.priceSource)
  const priceSource2 = useAppSelector(state => state.collectionV2.priceSource2)
  const ownsCollection = useAppSelector(state => state.collectionV2.ownsCollection)

  const autoColumns = useAppSelector(state => state.collectionV2.autoColumns)
  const columnData = useAppSelector(state => state.collectionV2.columnData)

  const visableColumns = autoColumns
    ? columnData.map(opt => ({ ...opt, enabled: AUTO_COLUMNS.includes(opt.key) }))
    : columnData

  const saturatedCollectionColumnInfo = generateSaturatedColumns(
    collectionCard,
    actions,
    localCardData,
    priceSource,
    priceSource2,
    tags,
    ownsCollection,
    autoColumns,
  )

  return (
    <GenericCursorCardWrapper
      skipOnTouch
      className={`
        ${styles.cardRow}
        ${localCardData.isMultiSelected ? styles.multiSelected : ''}
        ${extraData.className}
      `}
      card={collectionCard.card}
      isDragging={false}>
      <TableContent columns={visableColumns} saturatedColumns={saturatedCollectionColumnInfo} />
    </GenericCursorCardWrapper>
  )
}

export default TextCollectionCard

export const TextCollectionCardHeader = () => {
  const router = useRouter()

  const collectionOrderBy = `${router.query.collectionOrderBy || 'createdAt'}`
  const orderDirection = `${router.query.orderDirection || 'ascending'}` as 'ascending' | 'descending'

  const priceSource = useAppSelector(state => state.collectionV2.priceSource)
  const priceSource2 = useAppSelector(state => state.collectionV2.priceSource2)

  const autoColumns = useAppSelector(state => state.collectionV2.autoColumns)
  const columnData = useAppSelector(state => state.collectionV2.columnData)

  const [initialRender, setInitialRender] = useState(true)
  const [localOrdering, setLocalOrdering] = useState<{
    collectionOrderBy: string
    orderDirection: 'ascending' | 'descending'
    priceSource?: string
  }>({
    collectionOrderBy: collectionOrderBy,
    orderDirection: orderDirection,
  })

  const priceSourceOne = PRICE_CODES[priceSource]
  const priceSourceTwo = PRICE_CODES[priceSource2]

  const handleChangeSort = (sortBy: string, priceSource?: string) => {
    let orderDirection: 'ascending' | 'descending' = 'ascending'

    if (sortBy === localOrdering.collectionOrderBy && localOrdering.orderDirection === 'ascending')
      orderDirection = 'descending'

    setLocalOrdering({ collectionOrderBy: sortBy, orderDirection, priceSource })
  }

  const handleSetParams = (params: any) => setCollectionQueryParams(params)

  useEffect(() => {
    if (initialRender) {
      setInitialRender(false)

      return
    }

    const debouncedSearch = setTimeout(() => handleSetParams(localOrdering), 750)

    return () => clearTimeout(debouncedSearch)
  }, [localOrdering])

  const saturatedHeaderInfo = generateSaturatedHeaders(
    handleChangeSort,
    localOrdering.collectionOrderBy,
    localOrdering.orderDirection,
    priceSourceOne,
    priceSourceTwo,
    autoColumns,
  )
  const visableColumns = autoColumns
    ? columnData.map(opt => ({ ...opt, enabled: AUTO_COLUMNS.includes(opt.key) }))
    : columnData

  return (
    <div className={styles.header}>
      <TableContent columns={visableColumns} saturatedColumns={saturatedHeaderInfo} />
    </div>
  )
}

const TableContent = ({
  columns,
  saturatedColumns,
}: {
  columns: CollectionColumnOptions[]
  saturatedColumns: Partial<Record<CollectionColumn, SaturatedCollectionColumnOptions>>
}) => (
  <>
    {columns.map((column, index) => {
      if (!column.enabled) return null

      const saturatedData = saturatedColumns[column.key]

      // @ts-ignore
      const onClick = saturatedData?.onClick || undefined

      // @ts-ignore
      const className = saturatedData?.className || undefined

      // @ts-ignore
      const content = saturatedData?.content ?? saturatedData

      const width = column.width || 100
      const style: React.CSSProperties = { width: width }

      if (!onClick)
        return (
          <span key={index} className={`${styles.defaultColumn} ${className}`} style={style}>
            {content}
          </span>
        )

      return (
        <button key={index} onClick={onClick} className={`${styles.defaultColumn} ${className}`} style={style}>
          {content}
        </button>
      )
    })}
  </>
)
